import { useMemo } from "react";
import moment from "moment";
import { CustomCellRendererProps } from "ag-grid-react";
import { Box, Typography, useTheme } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { ConsultantSearchQualityCheckDataFragment } from "@bonliva-crm/graphql";
import locale, { dateFormat } from "@bonliva-crm/locale";
import { WithPopper } from "@bonliva-crm/ui";
import { ConsultantRowData } from "../../../utils/consultant-row-data";
import { SaHlrModal } from "../../sa-hlr-modal/sa-hlr-modal";

type Props = CustomCellRendererProps<ConsultantRowData>;

const checkNames = ["S-HLR Barn", "S-HLR Vuxen", "A-HLR Barn", "A-HLR Vuxen"];
const statusesByPriority = [
  "Ordered",
  "ConsultantResponsible",
  "Booked",
  "Approved",
  "AwaitingApproval"
];

export function SaHlrRenderer(props: Props) {
  const theme = useTheme();

  const qc = useMemo(() => {
    let currentStatusIndex = -1;
    let qcc: ConsultantSearchQualityCheckDataFragment | null = null;

    const allQualityChecks = props.data?.qualityChecks || [];

    for (const check of allQualityChecks) {
      if (check.country !== "Sweden" || !checkNames.includes(check.itemName)) {
        continue;
      }

      if (!check.status) {
        continue;
      }

      const statusIndex = statusesByPriority.indexOf(check.status);

      if (statusIndex > currentStatusIndex) {
        currentStatusIndex = statusIndex;
        qcc = check;
      }
    }

    return qcc;
  }, [props.data?.qualityChecks]);

  if (!qc) {
    return null;
  }

  const setOn = qc.setDate ? moment(qc.setDate).format(dateFormat) : "";
  const expiresOn = qc.expirationDate ? moment(qc.expirationDate).format(dateFormat) : "";

  let bgColor = undefined;

  if (qc.status === "Approved") {
    bgColor = theme.palette.success.light;
  }

  if (qc.expirationDate && new Date(qc.expirationDate) < new Date()) {
    bgColor = theme.palette.error.light;
  }

  return (
    <Box style={{ backgroundColor: bgColor }} height="100%" width="100%" p={0.5}>
      <Typography variant="body2" display="block">
        {locale.qualityStatuses[qc.status] || qc.status} ({qc.itemName})
      </Typography>

      {setOn && (
        <Typography variant="caption" display="block">
          {locale.Date}: {setOn}
        </Typography>
      )}

      {expiresOn && (
        <Typography variant="caption" display="block">
          {locale.Expires}: {expiresOn}
        </Typography>
      )}

      <Box position="absolute" bottom={4} right={8}>
        <WithPopper
          behaviour="hover"
          popperContent={<SaHlrModal open={true} consultantId={props.data?._id} />}
          PopperProps={{
            popperOptions: {
              placement: "auto"
            },
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: "20,20"
                }
              },
              {
                name: "arrow",
                options: {
                  enabled: true
                }
              }
            ]
          }}
          rootStyle={{ height: "100%" }}
        >
          {({ open }) => {
            return <InfoOutlined color={open ? "action" : "disabled"} />;
          }}
        </WithPopper>
      </Box>
    </Box>
  );
}
